import {
  getOnboarding as apiGetOnboarding,
  completeOnboarding as apiCompleteOnboarding,
  type OnboardingState,
} from "./blynApiClient";
import { store } from "@/blyn/legacy/state/store";
import { toastApiError } from "./apiToast";
import { resetOnboarding as apiResetOnboarding, type OnboardingInsight } from "./onboardingApi";
import { getTenantId as resolveTenantId } from "./currentUser";


export interface OnboardingChoices {
  companyName?: string;
  vertical?: string;
  connectedSource?: string;
  connectedIntegrationId?: string;
  brandVoiceFile?: string;
  brandVoiceKbId?: string;
  useCase?: string;
}

let cachedTenantId: string | null = null;
let inflightLoad: Promise<OnboardingState | null> | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSave: { choices: OnboardingChoices; step: number } | null = null;

function getTenantId() {
  return resolveTenantId();
}

function isPreviewMode() {
  return store.get("onboardingPreviewMode") === true;
}

function stateFromStore(): OnboardingState {
  const choices = (store.get("onboardingChoices") || {}) as OnboardingChoices;
  return {
    completed: store.get("onboardingComplete") === true,
    company_name: choices.companyName ?? null,
    vertical: choices.vertical ?? null,
    use_case: choices.useCase ?? null,
    connected_source: choices.connectedSource ?? null,
    connected_integration_id: choices.connectedIntegrationId ?? null,
    brand_voice_kb_id: choices.brandVoiceKbId ?? null,
    brand_voice_file_name: choices.brandVoiceFile ?? null,
    step: store.get("onboardingStep") ?? null,
  };
}

function applyStateToStore(state: OnboardingState) {
  if (isPreviewMode()) return;

  const choices: OnboardingChoices = {
    companyName: (state.company_name && state.company_name !== "pending") ? state.company_name : undefined,
    vertical: (state.vertical && state.vertical !== "pending") ? state.vertical : undefined,
    useCase: (state.use_case && state.use_case !== "pending") ? state.use_case : undefined,
    connectedSource: state.connected_source ?? undefined,
    connectedIntegrationId: state.connected_integration_id ?? undefined,
    brandVoiceKbId: state.brand_voice_kb_id ?? undefined,
    brandVoiceFile: state.brand_voice_file_name ?? undefined,
  };
  store.set({
    onboardingComplete: state.completed,
    onboardingStep: state.completed ? 5 : (state.step ?? 1),
    onboardingChoices: choices,
    onboardingHydrated: true,
  });
  if (state.completed) {
    localStorage.setItem("blyn-onboarded", "1");
  } else {
    localStorage.removeItem("blyn-onboarded");
  }
}

function choicesToBody(choices: OnboardingChoices, step?: number, partial?: boolean) {
  return {
    company_name: choices.companyName || "pending",
    vertical: choices.vertical || "pending",
    use_case: choices.useCase || "pending",
    connected_source: choices.connectedSource ?? null,
    connected_integration_id: choices.connectedIntegrationId ?? null,
    brand_voice_kb_id: choices.brandVoiceKbId ?? null,
    brand_voice_file_name: choices.brandVoiceFile ?? null,
    step: step ?? null,
    partial,
  };
}

export function invalidateOnboardingCache() {
  cachedTenantId = null;
  inflightLoad = null;
}

export function isOnboardingCompleteLocally(): boolean {
  if (isPreviewMode()) return false;
  return !!localStorage.getItem("blyn-onboarded") || store.get("onboardingComplete") === true;
}

export function shouldShowOnboardingModal(): boolean {
  if (isPreviewMode()) return true;
  if (!store.get("onboardingHydrated")) return false;
  return store.get("onboardingComplete") !== true;
}

/** Pre-fill new-conversation composer after onboarding step 5 CTA. */
export function buildOnboardingChatSeed(choices: OnboardingChoices, cta: string): string {
  const company = choices.companyName?.trim();
  const source = choices.connectedSource?.trim();
  const parts = [cta.replace(/\.$/, "")];
  if (company) parts.push(`for ${company}`);
  if (source) parts.push(`using ${source}`);
  return `${parts.join(" ")}.`;
}

export function seedComposerFromOnboarding(
  choices: OnboardingChoices,
  cta: string | { label: string; prompt?: string },
  insight?: OnboardingInsight | null
): void {
  let prompt =
    typeof cta === "object" && cta.prompt
      ? cta.prompt
      : buildOnboardingChatSeed(choices, typeof cta === "string" ? cta : cta.label);

  if (insight) {
    prompt = `${prompt}\n\nContext from onboarding insight:\n- Title: ${insight.title}\n- Findings: ${insight.body}\n- Trace: ${insight.trace}`;
  }

  store.set({ composerDraftSeed: prompt });
}

export function choicesToInsightRequest(choices: OnboardingChoices): import("./onboardingApi.types").OnboardingInsightRequest {
  return {
    company_name: choices.companyName || undefined,
    vertical: choices.vertical || undefined,
    use_case: choices.useCase || undefined,
    connected_source: choices.connectedSource,
    connected_integration_id: choices.connectedIntegrationId,
    brand_voice_kb_id: choices.brandVoiceKbId,
    brand_voice_file_name: choices.brandVoiceFile,
  };
}

/** Local-only replay/review of the wizard for users who already completed onboarding. */
export function enterOnboardingPreview() {
  if (isPreviewMode()) return;

  const existing = (store.get("onboardingChoices") || {}) as OnboardingChoices;
  const wasComplete =
    store.get("onboardingComplete") === true || !!localStorage.getItem("blyn-onboarded");

  // Preview is a local-only replay of the wizard UI — it must not clear the
  // persisted completion flag. If the tab closes (or logout hard-navigates)
  // before exitOnboardingPreview() runs, a cleared flag would permanently
  // send this user back to onboarding on every subsequent login.
  store.set({
    onboardingPreviewMode: true,
    onboardingStep: wasComplete ? 5 : (store.get("onboardingStep") ?? 1),
    onboardingChoices: { ...existing },
    onboardingComplete: false,
    onboardingHydrated: true,
  });
}

/** Leave preview and restore server onboarding state (one GET). */
export async function exitOnboardingPreview(): Promise<void> {
  if (!isPreviewMode()) return;
  store.set({ onboardingPreviewMode: false });
  invalidateOnboardingCache();
  await loadOnboarding({ quiet: true, force: true });
}

export function resetOnboardingPreview() {
  enterOnboardingPreview();
}

export async function loadOnboarding(options: { quiet?: boolean; force?: boolean } = {}): Promise<OnboardingState | null> {
  if (isPreviewMode() && !options.force) {
    return stateFromStore();
  }

  const tenantId = getTenantId();

  if (options.force) {
    cachedTenantId = null;
    inflightLoad = null;
  }

  if (!options.force && cachedTenantId === tenantId && store.get("onboardingHydrated")) {
    return stateFromStore();
  }

  if (inflightLoad && !options.force) {
    return inflightLoad;
  }

  inflightLoad = (async () => {
    try {
      const state = await apiGetOnboarding(tenantId);
      applyStateToStore(state);
      cachedTenantId = tenantId;
      return state;
    } catch (err) {
      if (!options.quiet) {
        toastApiError("Failed to load onboarding", err);
      }
      if (!isPreviewMode()) {
        // A failed refresh (e.g. a transient network/auth hiccup right after
        // login) must not downgrade a user we already know completed
        // onboarding — only fall back to "not onboarded" when we have no
        // prior signal at all, otherwise every flaky load would re-trigger
        // the first-run wizard for an already-onboarded user.
        if (isOnboardingCompleteLocally()) {
          store.set({ onboardingHydrated: true });
        } else {
          store.set({ onboardingHydrated: true, onboardingComplete: false });
          localStorage.removeItem("blyn-onboarded");
        }
      }
      cachedTenantId = tenantId;
      return null;
    } finally {
      inflightLoad = null;
    }
  })();

  return inflightLoad;
}

async function flushOnboardingProgress(choices: OnboardingChoices, step: number): Promise<void> {
  try {
    await apiCompleteOnboarding(getTenantId(), {
      ...choicesToBody(choices, step),
      partial: true,
    });
  } catch (err) {
    toastApiError("Failed to save onboarding progress", err);
  }
}

export function saveOnboardingProgress(choices: OnboardingChoices, step: number, immediate = false): void {
  if (isPreviewMode()) return;

  pendingSave = { choices, step };
  if (saveTimer) clearTimeout(saveTimer);

  if (immediate) {
    saveTimer = null;
    void flushOnboardingProgress(choices, step);
    pendingSave = null;
    return;
  }

  saveTimer = setTimeout(() => {
    saveTimer = null;
    if (!pendingSave) return;
    void flushOnboardingProgress(pendingSave.choices, pendingSave.step);
    pendingSave = null;
  }, 400);
}

export async function skipOnboarding(choices: OnboardingChoices): Promise<boolean> {
  if (isPreviewMode()) {
    store.set({ onboardingPreviewMode: false, onboardingComplete: true, onboardingStep: 5 });
    localStorage.setItem("blyn-onboarded", "1");
    return true;
  }

  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  pendingSave = null;

  try {
    const state = await apiCompleteOnboarding(getTenantId(), {
      ...choicesToBody(choices, 5),
      partial: true,
    });
    applyStateToStore({ ...state, completed: true });
    store.set({ onboardingComplete: true, onboardingStep: 5, onboardingHydrated: true });
    localStorage.setItem("blyn-onboarded", "1");
    cachedTenantId = getTenantId();
    return true;
  } catch (err) {
    toastApiError("Failed to skip onboarding", err);
    return false;
  }
}

export async function finishOnboarding(choices: OnboardingChoices): Promise<boolean> {
  if (isPreviewMode()) {
    store.set({ onboardingPreviewMode: false, onboardingComplete: true });
    localStorage.setItem("blyn-onboarded", "1");
    return true;
  }

  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  if (pendingSave) {
    await flushOnboardingProgress(pendingSave.choices, pendingSave.step);
    pendingSave = null;
  }

  try {
    const state = await apiCompleteOnboarding(getTenantId(), choicesToBody(choices, 5));
    applyStateToStore(state);
    localStorage.setItem("blyn-onboarded", "1");
    store.set({ onboardingComplete: true, onboardingHydrated: true });
    cachedTenantId = getTenantId();
    return true;
  } catch (err) {
    toastApiError("Failed to complete onboarding", err);
    return false;
  }
}

export async function resetOnboarding(): Promise<boolean> {
  try {
    await apiResetOnboarding();
    store.set({
      onboardingComplete: false,
      onboardingStep: 1,
      onboardingChoices: {},
    });
    localStorage.removeItem("blyn-onboarded");
    invalidateOnboardingCache();
    return true;
  } catch (err) {
    toastApiError("Failed to reset onboarding", err);
    return false;
  }
}


export {
  getOnboardingOptions,
  listOnboardingIntegrations,
  connectOnboardingIntegration,
  uploadOnboardingBrandVoice,
  generateOnboardingInsight,
  ONBOARDING_USE_MOCK_API,
  type OnboardingInsight,
  type OnboardingIntegrationOption,
  type OnboardingOptionsResponse,
  type OnboardingUseCaseOption,
  type OnboardingVerticalOption,
} from "./onboardingApi";
